import { ArrowRight, Film, ShieldCheck, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

import { WaitlistTrigger } from '../components/WaitlistTrigger';

const heroSlides = [
  {
    src: '/images/jogger-wrap.JPG',
    alt: 'Wrap photo from Jogger, a short film by the Filmspo team',
    caption: (
      <>
        Members of the Filmspo team wrapping on short film &ldquo;<em>Jogger</em>&rdquo;
      </>
    ),
  },
  {
    src: 'https://filmspo-prod.nyc3.cdn.digitaloceanspaces.com/images/cccDuo.jpg',
    alt: 'The Circle City Coders cofounders, developers of Filmspo',
    caption: <>Developer duo behind Filmspo (Greg Goodin left, Stuart Rimstidt right)</>,
  },
  {
    src: 'https://filmspo-prod.nyc3.cdn.digitaloceanspaces.com/images/mwff2026.jpg',
    alt: 'Midwest Fear Fest 2025',
    caption: <>Members of the Filmspo team at Midwest Fear Fest 2026</>,
  },
  {
    src: 'https://filmspo-prod.nyc3.cdn.digitaloceanspaces.com/images/lntWrap.jpg',
    alt: 'The Filmspo team',
    caption: (
      <>
        Members of the Filmspo team wrapping on short film &ldquo;<em>Leave No Trace</em>&ldquo;
      </>
    ),
  },
  {
    src: 'https://filmspo-prod.nyc3.cdn.digitaloceanspaces.com/images/mwffposterinsta.jpg',
    alt: 'The Filmspo team',
    caption: <>Theater poster at Midwest Fear Fest 2025</>,
  },
];

const values = [
  {
    icon: Film,
    title: 'Filmmaker-first',
    description:
      'Every product decision starts with the person making the work — not the platform, not the algorithm.',
  },
  {
    icon: ShieldCheck,
    title: 'Festival integrity',
    description:
      'Only verified, legitimate festivals appear on Filmspo. No pay-to-play placement, no unvetted listings.',
  },
  {
    icon: Users,
    title: 'Community-driven',
    description: 'Features are shaped by real feedback from filmmakers and festival organizers.',
  },
];

const milestones = [
  {
    year: 'Spring 2023',
    label: 'Making our first film',
    body: 'After years of making lighthearted videos, the team behind Filmspo made our first serious short film for festival submission.',
  },
  {
    year: 'Fall 2023',
    label: 'Midwest Fear Fest',
    body: "Filmspo board member Victoria Harris founded Midwest Fear Fest as Indianapolis' premier horror short film festival.",
  },
  {
    year: '2024',
    label: 'Frustration and finding',
    body: 'Between fielding submissions for Midwest Fear Fest and submitting our own films via Filmfreeway, the team learned about the challenges of navigating submission platforms as both filmmakers and festival organizers.',
  },
  {
    year: '2025',
    label: 'Development begins',
    body: 'After networking at other festivals and meeting other frustrated filmmakers, the team decided to develop a better platform for people like us.',
  },
  {
    year: '2026',
    label: 'Filmspo is born',
    body: 'After building, troubleshooting, iterating and sifting through feedback, Filmspo beta was scheduled for release on October 31st 2026.',
  },
];

export function AboutPage() {
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide(i => (i + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="about-page">
      {/* Hero */}
      <section className="about-hero" aria-labelledby="about-heading">
        <div className="cinema-grain" aria-hidden="true" />
        <div className="about-hero-content page-container">
          <div className="about-hero-copy">
            <p className="section-kicker hero-kicker">
              <span>Filmspo</span>
              The story behind the platform
            </p>
            <h1 id="about-heading">
              Built by filmmakers
              <em>and fest organizers.</em>
            </h1>
            <p className="about-hero-tagline">
              Filmspo started as a frustrated filmmaker&apos;s side project and grew into a full
              platform for the independent film community.
            </p>
          </div>

          <div className="about-hero-carousel">
            <div className="about-hero-carousel-track">
              {heroSlides.map((slide, i) => (
                <figure
                  key={i}
                  className={`about-hero-slide${activeSlide === i ? ' is-active' : ''}`}
                  aria-hidden={activeSlide !== i}
                >
                  <img src={slide.src} alt={slide.alt} />
                  <figcaption>{slide.caption}</figcaption>
                </figure>
              ))}
            </div>
            <div className="about-hero-carousel-dots" aria-label="Image navigation">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  className={activeSlide === i ? 'is-active' : undefined}
                  onClick={() => setActiveSlide(i)}
                  aria-label={`Go to image ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-shell about-mission" aria-labelledby="mission-heading">
        <div className="page-container">
          <p className="section-kicker">
            {/* <span>01</span> */}
            Our mission
          </p>
          <blockquote className="about-mission-statement" id="mission-heading">
            To build a better home for independent film and the festivals that champion it - a
            platform where filmmakers are respected, festivals are verified, and community is a
            priority.
          </blockquote>

          <div className="about-values">
            {values.map(({ icon: Icon, title, description }) => (
              <div key={title} className="about-value-card">
                <div className="about-value-icon">
                  <Icon aria-hidden="true" />
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand identity strip */}
      <section className="about-identity" aria-label="Brand identity">
        <div className="page-container about-identity-grid">
          <div className="about-identity-copy">
            <p className="section-kicker">
              {/* <span>02</span> */}
              Who we are
            </p>
            <h2>A platform with skin in the game.</h2>
            <p>
              Filmspo is the result of a partnership between{' '}
              <a
                href="https://circlecitycoders.com"
                target="_blank"
                rel="noopener noreferrer"
                className="about-link"
              >
                Circle City Coders
              </a>{' '}
              and{' '}
              <a
                href="https://midwestfearfest.com"
                target="_blank"
                rel="noopener noreferrer"
                className="about-link"
              >
                Midwest Fear Fest
              </a>
              , both based in Indianapolis, Indiana. We&apos;re not a venture-backed startup
              optimising for growth at any cost. We&apos;re a small team that loves film and is
              building a platform we&apos;d actually want to use ourselves.
            </p>
          </div>
          <div className="about-identity-badge" aria-hidden="true">
            <img src="/images/filmspo-logo-white.png" alt="" />
            <img src="/images/filmspo-logo-white.png" alt="" />
            <img src="/images/filmspo-logo-white.png" alt="" />
            <img src="/images/filmspo-logo-white.png" alt="" />
            <img src="/images/filmspo-logo-white.png" alt="" />
          </div>
        </div>
      </section>

      {/* History / Timeline */}
      <section className="section-shell about-story" aria-labelledby="story-heading">
        <div className="page-container">
          <p className="section-kicker">
            {/* <span>03</span> */}
            How we got here
          </p>
          <h2 id="story-heading">
            The history
            <em>(so far.)</em>
          </h2>

          <div className="about-timeline">
            {milestones.map((m, i) => (
              <div key={m.year} className="about-timeline-item">
                <div className="about-timeline-year">
                  <span>{m.year}</span>
                </div>
                <div className="about-timeline-connector" aria-hidden="true">
                  <span className="about-timeline-dot" />
                  {i < milestones.length - 1 && <span className="about-timeline-line" />}
                </div>
                <div className="about-timeline-body">
                  <h3>{m.label}</h3>
                  <p>{m.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="closing-section about-cta">
        <div className="closing-filmstrip" aria-hidden="true">
          {Array.from({ length: 12 }, (_, i) => (
            <span key={i} />
          ))}
        </div>
        <div className="page-container closing-content">
          <p className="section-kicker">Join us</p>
          <h2>The story&apos;s still being written.</h2>
          <p>Get early access and help shape what Filmspo becomes.</p>
          <WaitlistTrigger source="about" className="button button-gold button-large">
            Join the Waitlist
            <ArrowRight aria-hidden="true" />
          </WaitlistTrigger>
        </div>
      </section>
    </div>
  );
}
